import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  Eye,
  Package,
  ShoppingCart,
  BarChart3,
  Settings,
  MapPin,
  Clock,
  Mail,
  Phone,
  Globe,
  Award,
  Users,
  Star,
  TrendingUp,
  Calendar,
  Printer,
  Truck,
  CreditCard,
  Warehouse,
  Search,
  Filter,
  Bell,
  Copy,
  ToggleLeft,
  ToggleRight,
  Euro,
  Shield,
  Info,
  Layers,
  Sparkles,
  Navigation,
  Tag,
  MessageCircle,
  Key,
  Banknote,
  UtensilsCrossed,
  UserCheck,
  Gift,
} from 'lucide-react';
import apiService from '../services/api';
import { isAssistanceActive, isPlatformIngredientsReadOnly } from '../utils/assistanceSession';
import {
  resolvePendingCreateTarget,
  resolvePendingEditTarget,
  scheduleOpenWhenReady,
} from '../utils/pendingManagerOpen';
import CommercantCommandes from './CommercantCommandes';
import CommercantAnalytics from '../components/CommercantAnalytics';
import HorairesManager from '../components/HorairesManager';
import ParametresAvances from '../components/ParametresAvances';
import ProduitsManager from '../components/ProduitsManager';
import CatalogueRubriquesManager from '../components/CatalogueRubriquesManager';
import ServicesManager from '../components/ServicesManager';
import RessourcesManager from '../components/RessourcesManager';
import ZonesLivraisonManager from '../components/ZonesLivraisonManager';
import ProgrammeFidelite from '../components/ProgrammeFidelite';
import FideliteSectionNav from '../components/fidelite/FideliteSectionNav';
import FideliteRecompenses from '../components/fidelite/FideliteRecompenses';
import {
  FIDELITE_SECTION_PROGRAMME,
  FIDELITE_SECTION_RECOMPENSES,
  resolveFideliteSection,
} from '../utils/recompensePresentation';
import ServicesConfigurationPanel from '../components/ServicesConfigurationPanel';
import PolitiquePaiementParModePanel from '../components/PolitiquePaiementParModePanel';
import FermeturesTemporaires from '../components/FermeturesTemporaires';
import ReservationsTablesManager from '../components/ReservationsTablesManager';
import RendezVousManager from '../components/RendezVousManager';
import AvisManager from '../components/AvisManager';
import CloudinaryUpload from '../components/CloudinaryUpload';
import ImageSyncStatus from '../components/ImageSyncStatus';
import ClickAndCollectManager from '../components/ClickAndCollectManager';
import ExpeditionManager from '../components/ExpeditionManager';
import ShippingIntegrationPanel from '../components/ShippingIntegrationPanel';
import LivraisonManager from '../components/LivraisonManager';
import Modal from '../components/Modal';
import PromotionsManager from '../components/promotions/PromotionsManager';
import IngredientsManager from '../components/IngredientsManager';
import Notifications from './Notifications';
import CommercantSettingsPanel from '../components/settings/CommercantSettingsPanel.jsx';
import LivreursManager from '../components/LivreursManager';
import IdentifiantsCommercant from '../components/IdentifiantsCommercant';
import CommercantPrinterCockpit from '../components/CommercantPrinterCockpit';
import {
  buildImpressionDraftFromCommercant,
  buildImpressionUpdatePayload,
} from '../components/commercantImpressionDraft';
import {
  CLOUDINARY_ADMIN_WIDTH,
  cloudinaryDeliveryUrl,
} from '../utils/cloudinaryDelivery';
import { normalizeHoraires } from '../utils/normalizeHoraires';
import { frontendToBackend, hasItinerantRetraitAddress } from '../utils/transformHoraires';
import { buildNavigationLinks } from '../utils/navigation';
import { buildAnalyticsUrl } from '../utils/analyticsNavigation';
import CommercantSetupChecklist from '../components/CommercantSetupChecklist';
import { getAccountStatusForCommercant, buildAccountsByCommercantId } from '../utils/commercantListUtils';
import { usePermissions } from '../hooks/usePermissions';
import PremiumFeatureCard from '../components/PremiumFeatureCard';
import CommercantLogoField from '../components/CommercantLogoField';
import DisabledServiceFeatureCard from '../components/DisabledServiceFeatureCard';
import CartesCadeauxManager from '../components/cartesCadeaux/CartesCadeauxManager';
import PermissionModal from '../components/permissions/PermissionModal';
import CommunicationOfferAdminPanel from '../components/communication/CommunicationOfferAdminPanel';
import CommunicationBonusAdminPanel from '../components/communication/CommunicationBonusAdminPanel';
import {
  mergeWithRequiredPermissions,
  normalizePermissionsFromApi,
  sanitizePermissionsForSave,
  hasAnyPosPermission,
  canManagePlatformModules,
} from '../config/permissionDefinitions';
import { shouldShowStockNavigation, isStockManagementEnabled, canUseStockManagement } from '../utils/commercantServices';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import OverviewStatDetail, { METRIC_CONFIG } from '../components/OverviewStatDetail';
import {
  ActiviteByTypePanel,
  M1_LABEL_FALLBACK,
} from '../components/AnalyticsRegistreBanner';
import { formatEuro } from '../utils/overviewStats';
import {
  formatMonthPeriodLabel,
  getCurrentMonthToDateBounds,
} from '../utils/currentMonthPeriod';
import { buildCommercantPublicUrl } from '../components/prestation-wizard/buildWizardSiteUrls';
import CoordinateQualityPanel from '../components/CoordinateQualityPanel';
import { stripCoordinateFieldsFromOverviewPayload, canShowAdminActions } from '../utils/coordinateQualityUi';
import { resolveAdminCatalogKpis } from '../utils/catalogKpiCounts';
import CommerceMetierBadge from '../components/CommerceMetierBadge';
import useCommerceTaxonomy from '../hooks/useCommerceTaxonomy';
import { getCommercantCategoryIds } from '../config/commerce-categories';

const CommercantDetail = ({ setImpersonatedUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toasts, toast, removeToast } = useToast();
  const { getMetier: getTaxonomyMetier } = useCommerceTaxonomy();
  const [assistanceStarting, setAssistanceStarting] = useState(false);
  const [commercant, setCommercant] = useState(null);
  const [produits, setProduits] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [overview30j, setOverview30j] = useState(null);
  const [overviewMois, setOverviewMois] = useState(null);
  const [accountStatus, setAccountStatus] = useState('UNKNOWN');
  const [growthOffer, setGrowthOffer] = useState(null);
  const [nr2jLinkedOrgs, setNr2jLinkedOrgs] = useState([]);
  const [zonesLivraison, setZonesLivraison] = useState([]);
  const [fermeturesTemporaires, setFermeturesTemporaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) return tabFromUrl;
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('tab') || 'overview';
    }
    return 'overview';
  });
  const previousTabRef = useRef(null);
  // F2.2a — sous-section Fidélité (Mon programme | Récompenses)
  const fideliteSection = resolveFideliteSection(searchParams.get('section'));
  const setFideliteSection = useCallback(
    (section) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'fidelite');
      if (section === FIDELITE_SECTION_RECOMPENSES) {
        next.set('section', FIDELITE_SECTION_RECOMPENSES);
      } else {
        next.delete('section');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [userRole, setUserRole] = useState('ADMIN'); // À récupérer depuis le contexte d'auth
  const [savingHoraires, setSavingHoraires] = useState(false);
  const [savingAdvanced, setSavingAdvanced] = useState(false);
  const [savingImpression, setSavingImpression] = useState(false);
  const [impressionDraft, setImpressionDraft] = useState(() =>
    buildImpressionDraftFromCommercant(null)
  );
  const [appKeySaveNonce, setAppKeySaveNonce] = useState(0);
  const [appKeySavedOnLastSave, setAppKeySavedOnLastSave] = useState(false);
  const [savingServices, setSavingServices] = useState(false);
  const [savingModules, setSavingModules] = useState(false);
  const [modulesDraft, setModulesDraft] = useState({
    moduleProduits: true,
    modulePrestations: false,
    moduleCaisse: false,
    moduleB2b: false,
    moduleExpedition: false,
  });
  const [modulesSaved, setModulesSaved] = useState({
    moduleProduits: true,
    modulePrestations: false,
    moduleCaisse: false,
    moduleB2b: false,
    moduleExpedition: false,
  });
  const servicesManagerRef = useRef(null);
  const produitsManagerRef = useRef(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [pendingCreationType, setPendingCreationType] = useState(null);
  /** Options de create Prestation (ex. reservationEnLigne) — consommées à l’open. */
  const pendingPrestationCreateOptsRef = useRef(null);
  /** Edit Prestation après conversion / catalogue — orchestration scheduleOpenWhenReady. */
  const [pendingEditPrestationId, setPendingEditPrestationId] = useState(null);
  const [savingFermetures, setSavingFermetures] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [commercantPermissions, setCommercantPermissions] = useState([]);
  const [permissionModalCategory, setPermissionModalCategory] = useState('pos');
  // Refs pour éviter les boucles infinies entre URL et state
  const isUpdatingFromUrlRef = useRef(false);
  const isUpdatingToUrlRef = useRef(false);

  const mainNavigationLinks = commercant
    ? buildNavigationLinks({
        latitude: commercant.latitude,
        longitude: commercant.longitude,
        adresseLigne1: commercant.rue,
        codePostal: commercant.codePostal,
        ville: commercant.ville,
        libelleLieu: commercant.nom,
      })
    : [];

  const hasMainCoordinates =
    commercant?.latitude !== undefined &&
    commercant?.latitude !== null &&
    commercant?.longitude !== undefined &&
    commercant?.longitude !== null;

  const hasAdjustedCoordinates =
    commercant?.latitudeOverride !== undefined &&
    commercant?.latitudeOverride !== null &&
    commercant?.longitudeOverride !== undefined &&
    commercant?.longitudeOverride !== null;

  const formatCoordinate = (value) => {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '—';
    }
    return numeric.toFixed(6);
  };

  // État enregistré (stats, onglets) — ne change qu’après « Enregistrer les modules »
  const moduleProduitsEnabled = modulesSaved.moduleProduits ?? true;
  const moduleCaisseEnabled = modulesSaved.moduleCaisse ?? false;
  const modulePrestationsEnabled = modulesSaved.modulePrestations ?? false;

  const modulesDirty =
    modulesDraft.moduleProduits !== modulesSaved.moduleProduits ||
    modulesDraft.modulePrestations !== modulesSaved.modulePrestations ||
    modulesDraft.moduleCaisse !== modulesSaved.moduleCaisse ||
    modulesDraft.moduleB2b !== modulesSaved.moduleB2b ||
    modulesDraft.moduleExpedition !== modulesSaved.moduleExpedition;

  // Hook pour récupérer les permissions du commerçant
  const { hasPermission: checkPermission, loading: permissionsLoading } = usePermissions(id);
  
  // Récupérer l'utilisateur actuel (rôle et commercantId)
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };
  
  const currentUser = getCurrentUser();
  const currentUserRole = currentUser?.role || null;
  const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;
  
  // Vérifications de rôles
  const isSuperAdmin = () => {
    const role = currentUserRole || userRole;
    return role === 'SUPER_ADMIN' || role === 'super-admin';
  };

  const canEditPlatformModules = () => canManagePlatformModules(currentUserRole || userRole);

  useEffect(() => {
    if (!id || !isSuperAdmin()) {
      setGrowthOffer(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const ent = await apiService.getGrowthEntitlements(id);
        if (!cancelled) setGrowthOffer(ent);
      } catch {
        if (!cancelled) setGrowthOffer(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, currentUserRole]);

  useEffect(() => {
    if (!id || !isSuperAdmin()) {
      setNr2jLinkedOrgs([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.listNr2jOrganizationsForCommercant(id);
        if (!cancelled) setNr2jLinkedOrgs(res?.organizations || []);
      } catch {
        if (!cancelled) setNr2jLinkedOrgs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, currentUserRole]);

  // Fonction pour vérifier si le commerçant est ouvert maintenant
  const isOpenNow = (commercant) => {
    if (!commercant) return false;
    
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Dimanche, 1 = Lundi, etc.
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Minutes depuis minuit
    
    // Mapper les jours
    const joursMap = ['DIMANCHE', 'LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'];
    const jourActuel = joursMap[currentDay];
    
    // Chercher dans les horaires normalisés
    if (commercant.horaires && Array.isArray(commercant.horaires)) {
      const horairesDuJour = commercant.horaires.find(h => h.jour === jourActuel);
      
      if (horairesDuJour && horairesDuJour.creneaux && horairesDuJour.creneaux.length > 0) {
        // Vérifier si au moins un créneau est actif et correspond à l'heure actuelle
        return horairesDuJour.creneaux.some(creneau => {
          if (!creneau.actif) return false;
          
          const [heureDebut, minuteDebut] = (creneau.heureDebut || '').split(':').map(Number);
          const [heureFin, minuteFin] = (creneau.heureFin || '').split(':').map(Number);
          
          const debutMinutes = heureDebut * 60 + minuteDebut;
          const finMinutes = heureFin * 60 + minuteFin;
          
          return currentTime >= debutMinutes && currentTime <= finMinutes;
        });
      }
    }
    
    return false;
  };

  // Vérifier l'accès avant de charger les données
  useEffect(() => {
    // Vérifier si un COMMERCANT essaie d'accéder à un autre commerçant
    if (!isSuperAdmin() && currentUserRole === 'COMMERCANT') {
      if (userCommercantId && id !== userCommercantId) {
        toast.error('Accès refusé : vous ne pouvez accéder qu\'à votre propre commerce');
        navigate(`/commercants/${userCommercantId}`);
        return;
      }
    }
    fetchCommercantData();
  }, [id, currentUserRole, userCommercantId]);

  // Gérer les paramètres d'URL pour les onglets (URL -> State)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    // Rediriger 'services' vers 'prestations' si présent dans l'URL
    if (tabFromUrl === 'services') {
      if (!isUpdatingToUrlRef.current) {
        isUpdatingToUrlRef.current = true;
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', 'prestations');
        setSearchParams(newParams, { replace: true });
        setActiveTab('prestations');
        setTimeout(() => {
          isUpdatingToUrlRef.current = false;
        }, 100);
      }
      return;
    }
    
    // Si pas de tab dans l'URL, on doit être sur "overview"
    if (!tabFromUrl && activeTab !== 'overview' && !isUpdatingToUrlRef.current) {
      isUpdatingFromUrlRef.current = true;
      setActiveTab('overview');
      setTimeout(() => {
        isUpdatingFromUrlRef.current = false;
      }, 50);
      return;
    }
    
    if (tabFromUrl && tabFromUrl !== activeTab && !isUpdatingToUrlRef.current) {
      isUpdatingFromUrlRef.current = true;
      setActiveTab(tabFromUrl);
      // Réinitialiser le flag après un court délai
      setTimeout(() => {
        isUpdatingFromUrlRef.current = false;
      }, 50);
    }
  }, [searchParams, activeTab, setSearchParams]);

  // Écouter les événements personnalisés pour forcer le changement d'onglet
  useEffect(() => {
    const handleTabChange = (event) => {
      const { tab } = event.detail || {};
      if (tab && tab !== activeTab) {
        isUpdatingFromUrlRef.current = true;
        setActiveTab(tab);
        setTimeout(() => {
          isUpdatingFromUrlRef.current = false;
        }, 50);
      }
    };

    window.addEventListener('tabChange', handleTabChange);
    return () => window.removeEventListener('tabChange', handleTabChange);
  }, [activeTab]);

  // Mettre à jour l'URL quand l'onglet change (State -> URL, en préservant les autres paramètres)
  // MAIS seulement si le changement vient d'un clic utilisateur, pas d'une mise à jour depuis l'URL
  useEffect(() => {
    // Ignorer si on est en train de mettre à jour depuis l'URL ou si on vient de cliquer
    if (isUpdatingFromUrlRef.current || isUpdatingToUrlRef.current) {
      previousTabRef.current = activeTab;
      return;
    }
    
    const currentTab = searchParams.get('tab');
    const previousTab = previousTabRef.current;
    previousTabRef.current = activeTab;

    // Ne mettre à jour que si le tab a vraiment changé ET que ce n'est pas déjà dans l'URL
    if (activeTab && activeTab !== 'overview' && currentTab !== activeTab) {
      isUpdatingToUrlRef.current = true;
      // Préserver les autres paramètres (comme editPrestation)
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', activeTab);
      setSearchParams(newParams, { replace: true });
      setTimeout(() => {
        isUpdatingToUrlRef.current = false;
      }, 100);
    } else if (
      activeTab === 'overview' &&
      currentTab &&
      previousTab &&
      previousTab !== 'overview'
    ) {
      isUpdatingToUrlRef.current = true;
      // Uniquement si l'utilisateur a quitté un onglet vers Vue d'ensemble — jamais au F5.
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
      setTimeout(() => {
        isUpdatingToUrlRef.current = false;
      }, 100);
    }
  }, [activeTab, setSearchParams, searchParams]);

  useEffect(() => {
    const detail = searchParams.get('detail');
    if (detail && METRIC_CONFIG[detail] && activeTab !== 'overview') {
      setActiveTab('overview');
      return;
    }
    if (activeTab !== 'overview' && detail) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('detail');
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const triggerCreationFlow = (type, opts = {}) => {
    if (!type) return;
    if (type === 'prestation' && !modulePrestationsEnabled) return;
    if (type === 'produit' && !moduleProduitsEnabled) return;

    setSelectedItemType(type);
    setShowAddItemModal(false);
    setPendingCreationType(type);
    if (type === 'prestation') {
      pendingPrestationCreateOptsRef.current = {
        reservationEnLigne:
          opts.reservationEnLigne === undefined ? true : Boolean(opts.reservationEnLigne),
      };
    } else {
      pendingPrestationCreateOptsRef.current = null;
    }

    // Synchroniser tab state + URL atomiquement pour éviter la course URL→State
    // qui remettait l’onglet précédent avant openCreatePrestation.
    const nextTab = type === 'prestation' ? 'prestations' : type === 'produit' ? 'produits' : null;
    if (!nextTab) return;

    isUpdatingToUrlRef.current = true;
    setActiveTab(nextTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', nextTab);
    setSearchParams(newParams, { replace: true });
    setTimeout(() => {
      isUpdatingToUrlRef.current = false;
    }, 100);
  };

  const handleOpenAddItem = () => {
    if (!modulePrestationsEnabled && !moduleProduitsEnabled) {
      return;
    }

    // Si un seul module est activé, ouvrir directement sans modal de choix
    if (moduleProduitsEnabled && !modulePrestationsEnabled) {
      // Produits seulement : ouvrir directement le modal ProduitModal
      if (produitsManagerRef.current?.openCreateProduit) {
        produitsManagerRef.current.openCreateProduit();
      } else {
        // Fallback : changer d'onglet et déclencher l'ouverture
        setActiveTab('produits');
        triggerCreationFlow('produit');
      }
      return;
    }

    if (modulePrestationsEnabled && !moduleProduitsEnabled) {
      // Prestations seulement : ouvrir directement le modal PrestationModal
      setActiveTab('prestations');
      triggerCreationFlow('prestation');
      return;
    }

    // Les deux modules sont activés : afficher le modal de choix
    const defaultType = 'produit'; // Priorité aux produits
    setSelectedItemType(defaultType);
    setShowAddItemModal(true);
  };

  const fetchCommercantData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching commerçant with ID:', id);
      
      // Récupérer les données du commerçant
      const commercantData = await apiService.getCommercant(id);
      console.log('✅ Commerçant data received:', commercantData);
      const horairesUI = normalizeHoraires(commercantData?.horaires || []);
      setCommercant({ ...commercantData, horaires: horairesUI });
      const modulesSnapshot = {
        moduleProduits: commercantData?.moduleProduits ?? true,
        modulePrestations: commercantData?.modulePrestations ?? false,
        moduleCaisse: commercantData?.moduleCaisse ?? false,
        moduleB2b: commercantData?.moduleB2b ?? false,
        moduleExpedition: commercantData?.moduleExpedition ?? false,
      };
      setModulesSaved(modulesSnapshot);
      setModulesDraft(modulesSnapshot);
      setError('');
      setLoading(false);

      try {
        const permsRaw = await apiService.getCommercantPermissions(id);
        const permList = normalizePermissionsFromApi(
          (Array.isArray(permsRaw) ? permsRaw : []).map((p) =>
            typeof p === 'string' ? p : p.permission,
          ),
        );
        setCommercantPermissions(permList);
      } catch (permErr) {
        console.warn('Permissions non chargées:', permErr);
      }
      
      const produitsCommercant = commercantData?.produits || [];
      const prestationsCommercant = commercantData?.prestations || [];
      setProduits(produitsCommercant);
      setPrestations(prestationsCommercant);

      const catalogKpis = resolveAdminCatalogKpis(commercantData, {
        produits: produitsCommercant,
        prestations: prestationsCommercant,
      });
      
      // Récupérer les commandes du commerçant (non bloquant : la fiche ne reste pas en spinner)
      apiService.getCommandes({
        commercantId: id,
        pageSize: 200,
        excludeCanalVente: 'POS',
      }).then((commandesData) => {
        const commandesCommercant = commandesData.data?.filter(c => 
          c.commercantId === id || c.commercant?.id === id
        ) || [];
        setCommandes(commandesCommercant);
      }).catch((commandesErr) => {
        console.warn('Commandes commerçant non chargées:', commandesErr);
      });

      setAnalytics({
        totalProduits: catalogKpis.totalProduits,
        totalPrestations: catalogKpis.totalPrestations,
        noteMoyenne: commercantData?.note || 0,
      });

      try {
        const overview30 = await apiService.getCommercantAnalyticsOverview(id, { period: '30j' });
        setOverview30j(overview30);
      } catch (overviewErr) {
        console.warn('Overview 30j non chargé:', overviewErr);
        setOverview30j(null);
      }

      try {
        const monthBounds = getCurrentMonthToDateBounds();
        const overviewMonth = await apiService.getCommercantAnalyticsOverview(id, {
          dateDebut: monthBounds.dateDebut,
          dateFin: monthBounds.dateFin,
          timezone: monthBounds.timezone,
        });
        setOverviewMois(overviewMonth);
      } catch (overviewErr) {
        console.warn('Overview mois en cours non chargé:', overviewErr);
        setOverviewMois(null);
      }

      if (commercantData?.livraison) {
        try {
          const zones = await apiService.getZonesLivraison(id);
          const zonesList = Array.isArray(zones) ? zones : zones?.data || zones?.zones || [];
          setZonesLivraison(zonesList);
        } catch (err) {
          console.warn('Zones de livraison:', err);
          setZonesLivraison([]);
        }
      } else {
        setZonesLivraison([]);
      }

      try {
        if (isSuperAdmin()) {
          const summary = await apiService.getCommercantsAccountsSummary();
          const map = buildAccountsByCommercantId(summary?.items || []);
          setAccountStatus(getAccountStatusForCommercant(map, id));
        } else if (userCommercantId && String(userCommercantId) === String(id)) {
          // Gérant/staff sur sa propre boutique : compte lié par définition (session active)
          setAccountStatus('LINKED');
        } else {
          setAccountStatus('UNKNOWN');
        }
      } catch {
        setAccountStatus(
          userCommercantId && String(userCommercantId) === String(id) ? 'LINKED' : 'UNKNOWN'
        );
      }
      
      // Fermetures temporaires
      try {
        const fermeturesData = await apiService.getFermeturesTemporaires(id);
        setFermeturesTemporaires(fermeturesData);
      } catch (err) {
        console.warn('Erreur lors du chargement des fermetures temporaires:', err);
        setFermeturesTemporaires([]);
      }
      
      setError('');
    } catch (err) {
      console.error('❌ Error fetching commerçant:', err);
      setCommercant((current) => {
        if (current) return current;
        setError('Erreur lors du chargement des données: ' + err.message);
        return current;
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshOverview30j = async () => {
    if (!id) return;
    try {
      const overview30 = await apiService.getCommercantAnalyticsOverview(id, { period: '30j' });
      setOverview30j(overview30);
    } catch (overviewErr) {
      console.warn('Overview 30j refresh:', overviewErr);
    }
    try {
      const monthBounds = getCurrentMonthToDateBounds();
      const overviewMonth = await apiService.getCommercantAnalyticsOverview(id, {
        dateDebut: monthBounds.dateDebut,
        dateFin: monthBounds.dateFin,
        timezone: monthBounds.timezone,
      });
      setOverviewMois(overviewMonth);
    } catch (overviewErr) {
      console.warn('Overview mois en cours refresh:', overviewErr);
    }
  };

  const handleModuleDraftChange = (field, value) => {
    setModulesDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelModulesDraft = () => {
    setModulesDraft({ ...modulesSaved });
  };

  const handleSaveModules = async () => {
    const previousSaved = { ...modulesSaved };
    try {
      setSavingModules(true);
      const payload = {
        moduleProduits: Boolean(modulesDraft.moduleProduits),
        modulePrestations: Boolean(modulesDraft.modulePrestations),
        moduleCaisse: Boolean(modulesDraft.moduleCaisse),
        moduleB2b: Boolean(modulesDraft.moduleB2b),
      };
      if (isSuperAdmin()) {
        payload.moduleExpedition = Boolean(modulesDraft.moduleExpedition);
      }

      const response = await apiService.updateCommercant(id, payload);
      const updatedCommercant = response?.commercant || response || {};

      const savedModules = {
        moduleProduits: Boolean(
          updatedCommercant.moduleProduits ?? payload.moduleProduits,
        ),
        modulePrestations: Boolean(
          updatedCommercant.modulePrestations ?? payload.modulePrestations,
        ),
        moduleCaisse: Boolean(updatedCommercant.moduleCaisse ?? payload.moduleCaisse),
        moduleB2b: Boolean(updatedCommercant.moduleB2b ?? payload.moduleB2b),
        moduleExpedition: Boolean(
          updatedCommercant.moduleExpedition
            ?? (isSuperAdmin() ? payload.moduleExpedition : previousSaved.moduleExpedition),
        ),
      };
      setModulesSaved(savedModules);
      setModulesDraft(savedModules);
      setCommercant((prev) =>
        prev
          ? {
              ...prev,
              ...savedModules,
              ...updatedCommercant,
              horaires: prev.horaires,
            }
          : prev,
      );

      if (payload.moduleCaisse !== previousSaved.moduleCaisse) {
        try {
          const permsRaw = await apiService.getCommercantPermissions(id);
          const list = normalizePermissionsFromApi(
            (Array.isArray(permsRaw) ? permsRaw : []).map((p) =>
              typeof p === 'string' ? p : p.permission,
            ),
          );
          setCommercantPermissions(list);
        } catch (permErr) {
          console.warn('Resync permissions après module caisse:', permErr);
        }
      }

      if (payload.moduleProduits || payload.modulePrestations) {
        const full = await apiService.getCommercant(id);
        const data = full?.commercant || full;
        const horairesUI = normalizeHoraires(data?.horaires || []);
        setCommercant((prev) => ({
          ...prev,
          ...data,
          ...payload,
          horaires: horairesUI.length ? horairesUI : prev?.horaires,
        }));
        setProduits(payload.moduleProduits ? data?.produits || [] : []);
        setPrestations(payload.modulePrestations ? data?.prestations || [] : []);
        const catalogKpis = resolveAdminCatalogKpis(data, {
          produits: data?.produits || [],
          prestations: data?.prestations || [],
        });
        setAnalytics((prev) => ({
          ...prev,
          totalProduits: payload.moduleProduits ? catalogKpis.totalProduits : 0,
          totalPrestations: payload.modulePrestations ? catalogKpis.totalPrestations : 0,
        }));
      } else {
        setProduits([]);
        setPrestations([]);
        setAnalytics((prev) => ({ ...prev, totalProduits: 0, totalPrestations: 0 }));
      }

      window.dispatchEvent(
        new CustomEvent('commercantServicesUpdated', {
          detail: { commercantId: id, services: payload },
        }),
      );

      toast.success('Modules enregistrés avec succès');
    } catch (err) {
      console.error('Erreur mise à jour modules:', err);
      setModulesDraft({ ...modulesSaved });
      toast.error(err?.message || 'Impossible d’enregistrer les modules');
    } finally {
      setSavingModules(false);
    }
  };

  const handleOpenPermissions = async (initialCategory = 'pos') => {
    try {
      const permsRaw = await apiService.getCommercantPermissions(id);
      const list = normalizePermissionsFromApi(
        (Array.isArray(permsRaw) ? permsRaw : []).map((p) =>
          typeof p === 'string' ? p : p.permission,
        ),
      );
      setCommercantPermissions(list);
      setPermissionModalCategory(initialCategory || 'pos');
      setShowPermissionsModal(true);
    } catch (err) {
      console.error('Erreur chargement permissions:', err);
      toast.error('Impossible de charger les permissions');
    }
  };

  const handleSavePermissions = async (permissions) => {
    try {
      const cleaned = sanitizePermissionsForSave(permissions);
      await apiService.updateCommercantPermissions(id, cleaned);
      const hasPos = hasAnyPosPermission(cleaned);
      const nextModuleCaisse = hasPos;
      if (commercant?.moduleCaisse !== nextModuleCaisse) {
        setCommercant((prev) => (prev ? { ...prev, moduleCaisse: nextModuleCaisse } : prev));
        setModulesSaved((prev) => ({ ...prev, moduleCaisse: nextModuleCaisse }));
        setModulesDraft((prev) => ({ ...prev, moduleCaisse: nextModuleCaisse }));
      }
      setCommercantPermissions(cleaned);
      setShowPermissionsModal(false);
      toast.success('Permissions mises à jour');
    } catch (err) {
      console.error('Erreur sauvegarde permissions:', err);
      toast.error(err?.message || 'Erreur lors de la sauvegarde des permissions');
    }
  };

  const posModuleCoherent =
    !modulesSaved.moduleCaisse || hasAnyPosPermission(commercantPermissions);
  const posPermissionsCoherent =
    !hasAnyPosPermission(commercantPermissions) || modulesSaved.moduleCaisse;

  const handleEdit = () => {
    if (activeTab === 'overview') {
      setEditData({
        nom: commercant?.nom || '',
        email: commercant?.email || '',
        emailNotificationCommandes: commercant?.emailNotificationCommandes || '',
        telephone: commercant?.telephone || '',
        description: commercant?.description || '',
        siteWeb: commercant?.siteWeb || '',
        logo: commercant?.logo || '',
        banniere: commercant?.banniere || '',
        rue: commercant?.rue || '',
        ville: commercant?.ville || '',
        codePostal: commercant?.codePostal || '',
        tempsLivraisonMin: commercant?.tempsLivraisonMin || 30,
        tempsLivraisonMax: commercant?.tempsLivraisonMax || 60,
        minimumCommande: commercant?.minimumCommande || 0,
        fraisLivraison: commercant?.fraisLivraison || 0,
        paiementEnLigne: commercant?.paiementEnLigne || false,
        paiementLivraison: commercant?.paiementLivraison !== undefined ? commercant.paiementLivraison : true,
        paiementRetrait: commercant?.paiementRetrait !== undefined ? commercant.paiementRetrait : true,
        magasinItinerant: Boolean(commercant?.magasinItinerant),
        lieuPrincipal: commercant?.lieuPrincipal || '',
      });
      setEditing(true);
    } else {
      toast.warning(`Modification disponible uniquement depuis l'onglet "Vue d'ensemble"`);
    }
  };

  const handleSave = async () => {
    try {
      const payload = stripCoordinateFieldsFromOverviewPayload(editData);
      if (currentUserRole === 'COMMERCANT') {
        delete payload.nom;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'emailNotificationCommandes')) {
        const normalized = String(payload.emailNotificationCommandes ?? '').trim().toLowerCase();
        payload.emailNotificationCommandes = normalized || null;
      }
      await apiService.updateCommercant(id, payload);
      await fetchCommercantData();
      setEditing(false);
      setEditData({});
      toast.success('Commerçant mis à jour avec succès !');
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditData({});
  };

  // Fonction pour sauvegarder automatiquement le logo/bannière après upload
  const handleImageUpload = async (imageUrl, type) => {
    try {
      setUploadingImage(true);
      const updateData = { [type]: imageUrl };
      
      await apiService.updateCommercant(id, updateData);
      
      // Mettre à jour l'état local
      setCommercant({ ...commercant, [type]: imageUrl });
      setEditData({ ...editData, [type]: imageUrl });
      
      console.log(`✅ ${type} sauvegardé automatiquement:`, imageUrl);
    } catch (err) {
      console.error(`Erreur lors de la sauvegarde du ${type}:`, err);
      toast.error(`Erreur lors de la sauvegarde de l'image`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveHoraires = async (horairesFrontend) => {
    try {
      setSavingHoraires(true);
      
      // Transformer les données du format frontend vers le format backend
      const horairesBackend = frontendToBackend(horairesFrontend);
      
      console.log('Horaires transformés pour le backend:', horairesBackend);
      
      await apiService.updateCommercant(id, { horaires: horairesBackend });
      // Recharger les données du serveur pour avoir le bon format
      await fetchCommercantData();
      toast.success('Horaires mis à jour avec succès !');
      return true;
    } catch (err) {
      console.error('Erreur lors de la mise à jour des horaires:', err);
      const errorMessage = err?.response?.data?.error || err?.data?.error || err?.message || 'Erreur lors de la mise à jour des horaires';
      const errorDetails = err?.response?.data?.details || err?.data?.details;
      if (errorDetails && Array.isArray(errorDetails) && errorDetails.length > 0) {
        const detailsText = errorDetails.map(d => d.message || d).join('\n');
        toast.error(`${errorMessage}\n\nDétails:\n${detailsText}`);
      } else {
        toast.error(errorMessage);
      }
      return false;
    } finally {
      setSavingHoraires(false);
    }
  };

  const handleUpdateAdvanced = async (data) => {
    try {
      setSavingAdvanced(true);
      await apiService.updateCommercant(id, data);
      setCommercant({ ...commercant, ...data });
      toast.success('Paramètres avancés mis à jour avec succès !');
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingAdvanced(false);
    }
  };

  // Sync draft Impression depuis la fiche (App Key jamais préremplie).
  useEffect(() => {
    if (!commercant) return;
    setImpressionDraft(buildImpressionDraftFromCommercant(commercant));
  }, [commercant?.id, commercant?.imprimanteActive, commercant?.imprimanteNumeroSerie, commercant?.imprimanteAppId, commercant?.imprimerCommandeValidee, commercant?.imprimerCommandeAcceptee]);

  const handleSaveImpression = async () => {
    if (!id) return;
    try {
      setSavingImpression(true);
      const updateData = buildImpressionUpdatePayload(impressionDraft);
      await apiService.updateCommercant(id, updateData);
      setCommercant((prev) =>
        prev
          ? {
              ...prev,
              ...updateData,
              // Ne pas stocker la clé en clair dans le state fiche
              imprimanteAppKey: undefined,
            }
          : prev
      );
      const savedKey = Boolean(impressionDraft.imprimanteAppKey?.trim());
      setImpressionDraft((prev) => ({ ...prev, imprimanteAppKey: '' }));
      setAppKeySavedOnLastSave(savedKey);
      setAppKeySaveNonce((n) => n + 1);
      toast.success('Configuration impression enregistrée');
    } catch (err) {
      console.error('Erreur sauvegarde impression:', err);
      toast.error(
        err?.message || 'Erreur lors de l’enregistrement de l’impression'
      );
    } finally {
      setSavingImpression(false);
    }
  };

  const handleUpdateServices = async (serviceData) => {
    try {
      setSavingServices(true);
      await apiService.updateCommercant(id, serviceData);
      setCommercant({ ...commercant, ...serviceData });
      toast.success('Configuration des services mise à jour avec succès !');
      
      // Déclencher un événement pour mettre à jour le Layout
      window.dispatchEvent(new CustomEvent('commercantServicesUpdated', { 
        detail: { commercantId: id, services: serviceData } 
      }));
    } catch (err) {
      console.error('Erreur lors de la mise à jour des services:', err);
      toast.error('Erreur lors de la mise à jour des services');
      throw err; // Re-throw pour que le composant puisse gérer l'erreur
    } finally {
      setSavingServices(false);
    }
  };

  const handleUpdateFermetures = async (fermeturesData) => {
    try {
      setSavingFermetures(true);
      await apiService.updateFermeturesTemporaires(id, fermeturesData);
      setFermeturesTemporaires(fermeturesData);
      toast.success('Fermetures temporaires mises à jour avec succès !');
    } catch (err) {
      console.error('Erreur lors de la mise à jour des fermetures:', err);
      toast.error('Erreur lors de la mise à jour des fermetures');
      throw err; // Re-throw pour que le composant puisse gérer l'erreur
    } finally {
      setSavingFermetures(false);
    }
  };

  const handleClickAndCollectUpdate = (partial) => {
    setCommercant((prev) => (prev ? { ...prev, ...partial } : prev));
  };


  const handleDeleteCommercant = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce commerçant ?')) {
      try {
        await apiService.deleteCommercant(id);
        toast.success('Commerçant supprimé avec succès !');
        navigate('/commercants');
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const isCommercantAdmin = () => {
    const role = currentUserRole || userRole;
    return canShowAdminActions(role);
  };

  const statDetail = searchParams.get('detail');
  const validStatDetail = statDetail && METRIC_CONFIG[statDetail] ? statDetail : null;

  const openStatDetail = (metric) => {
    if (!METRIC_CONFIG[metric]) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'overview');
    newParams.set('detail', metric);
    setSearchParams(newParams);
    setActiveTab('overview');
  };

  const closeStatDetail = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('detail');
    setSearchParams(newParams, { replace: true });
  };

  const openTabFromStatDetail = (tabId) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('detail');
    newParams.set('tab', tabId);
    setSearchParams(newParams);
    setActiveTab(tabId);
  };

  const navigateChecklistItem = ({ tab = 'overview', section, href } = {}) => {
    if (href) {
      navigate(href);
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('detail');
    newParams.set('tab', tab);
    setSearchParams(newParams);
    setActiveTab(tab);
    if (section) {
      window.setTimeout(() => {
        document.getElementById(`setup-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
    }
  };

  const openOverviewSection = (section) => {
    navigateChecklistItem({ tab: 'overview', section });
  };

  const openCommandeDetail = (commande) => {
    if (!commande?.id) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('detail');
    newParams.set('tab', 'commandes');
    newParams.set('commandeId', commande.id);
    setSearchParams(newParams);
    setActiveTab('commandes');
  };

  const openProduitDetail = (produit) => {
    if (!produit?.id) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('detail');
    newParams.set('tab', 'produits');
    newParams.set('produitId', produit.id);
    setSearchParams(newParams);
    setActiveTab('produits');
  };

  const openPrestationDetail = (prestation) => {
    if (!prestation?.id) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('detail');
    newParams.set('tab', 'prestations');
    newParams.set('editPrestation', prestation.id);
    setSearchParams(newParams);
    setActiveTab('prestations');
  };

  const clearCommandeDetailFromUrl = () => {
    if (!searchParams.get('commandeId')) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('commandeId');
    setSearchParams(newParams, { replace: true });
  };

  const StatCardButton = ({ metric, className, children }) => (
    <button
      type="button"
      onClick={() => openStatDetail(metric)}
      className={`${className} w-full text-left cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2`}
      title="Voir le détail"
    >
      {children}
    </button>
  );

  // Configuration des onglets avec permissions (mémorisé)
  const allTabs = useMemo(() => {
    const livraisonEnabled = commercant?.livraison ?? false;
    const clickAndCollectEnabled = commercant?.clickAndCollect ?? false;
    const expeditionModuleEnabled = commercant?.moduleExpedition ?? false;

    return [
      { id: 'overview', label: 'Vue d\'ensemble', icon: Eye, permission: null }, // Toujours visible
      ...(modulePrestationsEnabled
        ? [{ id: 'prestations', label: 'Prestations', icon: Calendar, permission: null }] // Un seul onglet pour tout gérer
        : []),
      ...(commercant?.moduleCreneaux || commercant?.priseCreneaux
        ? [{ id: 'ressources', label: 'Ressources & créneaux', icon: MapPin, permission: null }]
        : []),
      ...(moduleProduitsEnabled
        ? [{ id: 'produits', label: 'Produits', icon: Package, permission: 'GESTION_PRODUITS' }]
        : []),
      { id: 'cartes-cadeaux', label: 'Cartes cadeaux', icon: Gift, permission: null },
      ...(shouldShowStockNavigation(commercant)
        ? [{ id: 'stock', label: 'Ingrédients & ruptures', icon: Warehouse, permission: 'GESTION_STOCK' }]
        : []),
      { id: 'commandes', label: 'Commandes', icon: ShoppingCart, permission: 'GESTION_COMMANDES' },
      { id: 'promotions', label: 'Promotions', icon: Tag, permission: 'GESTION_PROMOTIONS' },
      { id: 'avis', label: 'Avis', icon: Star, permission: 'GESTION_AVIS' },
      { id: 'analytics', label: 'Analytics', icon: BarChart3, permission: 'GESTION_ANALYTICS' },
      { id: 'fidelite', label: 'Fidélité', icon: Award, permission: 'GESTION_FIDELITE' },
      { id: 'communication', label: 'Communication', icon: Bell, permission: 'GESTION_NOTIFICATIONS' },
      { id: 'chat', label: 'Chat', icon: MessageCircle, permission: 'GESTION_CHAT' },
      // Click & Collect : affiché uniquement si le service est activé
      ...(clickAndCollectEnabled
        ? [{ id: 'clickandcollect', label: 'Click & Collect', icon: Truck, permission: null }]
        : []),
      ...(commercant?.reservationTable
        ? [{ id: 'reservations-tables', label: 'Réservations tables', icon: UtensilsCrossed, permission: null }]
        : []),
      // Livraison : affiché uniquement si le service est activé
      ...(livraisonEnabled
        ? [
            { id: 'livraison', label: 'Livraison', icon: Truck, permission: 'GESTION_LIVRAISON' },
            { id: 'livreurs', label: 'Livreurs', icon: Navigation, permission: 'GESTION_LIVRAISON' },
            { id: 'zones', label: 'Zones de livraison', icon: MapPin, permission: 'GESTION_LIVRAISON' }
          ]
        : []),
      ...(expeditionModuleEnabled
        ? [{ id: 'expedition', label: 'Expédition', icon: Package, permission: null }]
        : []),
      ...(isSuperAdmin() ? [
        { id: 'identifiants', label: 'Identifiants', icon: Key, permission: null },
        { id: 'impression', label: 'Impression', icon: Printer, permission: null },
        { id: 'advanced', label: 'Paramètres avancés', icon: Settings, permission: null }
      ] : []),
      { id: 'parametres', label: 'Paramètres', icon: Settings, permission: null }
    ];
  }, [moduleProduitsEnabled, modulePrestationsEnabled, isSuperAdmin, commercant?.livraison, commercant?.clickAndCollect, commercant?.moduleExpedition, commercant?.reservationTable, commercant?.moduleCreneaux, commercant?.priseCreneaux, commercant?.stock_management_enabled, commercant?.moduleProduits]);

  // Ne PAS filtrer les onglets - les montrer tous mais avec statut d'accès
  // Design premium toujours actif : visible mais grisé pour inciter à activer
  const tabs = useMemo(() => {
    return allTabs.map(tab => {
      // Calculer si l'onglet est accessible
      let hasAccess = true;
      if (tab.permission) {
        if (isSuperAdmin()) {
          hasAccess = true;
        } else {
          hasAccess = checkPermission(tab.permission);
        }
      }
      return {
        ...tab,
        hasAccess
      };
    });
  }, [allTabs, isSuperAdmin, checkPermission]);

  useEffect(() => {
    // Ne rien faire si les permissions sont encore en chargement
    if (permissionsLoading) {
      return;
    }

    // Vérifier si l'onglet actif est toujours autorisé
    const activeTabData = tabs.find(t => t.id === activeTab);
    if (activeTabData) {
      // Si l'onglet nécessite une permission et qu'on ne l'a pas :
      // On reste sur l'onglet pour montrer la card premium (design premium toujours actif)
      if (!activeTabData.hasAccess) {
        // On reste sur l'onglet pour montrer la card premium
        return;
      }
    }
    
    // Vérifications des modules
    // Rediriger 'services' vers 'prestations' une seule fois au chargement
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'services') {
      // Rediriger vers prestations si l'URL contient encore 'services'
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'prestations');
      setSearchParams(newParams, { replace: true });
      setActiveTab('prestations');
      return; // Sortir pour éviter les autres vérifications
    }
    
    if (activeTab === 'prestations' && !modulePrestationsEnabled) {
      setActiveTab('overview');
    } else if (activeTab === 'produits' && !moduleProduitsEnabled) {
      setActiveTab('overview');
    } else if (activeTab === 'stock' && !shouldShowStockNavigation(commercant)) {
      setActiveTab('overview');
    }
    
    // Vérifications des services (livraison, click & collect)
    const livraisonEnabled = commercant?.livraison ?? false;
    const clickAndCollectEnabled = commercant?.clickAndCollect ?? false;
    
    if (activeTab === 'clickandcollect' && !clickAndCollectEnabled) {
      setActiveTab('overview');
    } else if (
      (activeTab === 'livraison' || activeTab === 'livreurs' || activeTab === 'zones') &&
      !livraisonEnabled
    ) {
      setActiveTab('overview');
    } else if (activeTab === 'expedition' && !(commercant?.moduleExpedition ?? false)) {
      setActiveTab('overview');
    }
  }, [activeTab, modulePrestationsEnabled, moduleProduitsEnabled, permissionsLoading, tabs, commercant?.livraison, commercant?.clickAndCollect, commercant?.moduleExpedition, commercant?.stock_management_enabled, commercant?.moduleProduits, searchParams, setSearchParams]);

  useEffect(() => {
    // Gérer l'ouverture d'une prestation en mode édition depuis l'URL
    const editPrestationId = searchParams.get('editPrestation');
    if (editPrestationId && activeTab === 'prestations') {
      // Attendre que le composant ServicesManager soit monté
      const timer = setTimeout(() => {
        if (servicesManagerRef.current?.openEditPrestation) {
          servicesManagerRef.current.openEditPrestation(editPrestationId);
          // Retirer le paramètre de l'URL après ouverture (en préservant le tab)
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('editPrestation');
          // S'assurer que le tab est toujours présent
          if (activeTab && activeTab !== 'overview') {
            newParams.set('tab', activeTab);
          }
          setSearchParams(newParams, { replace: true });
        }
      }, 200); // Augmenter le délai pour être sûr que le composant est monté
      return () => clearTimeout(timer);
    }
    
    // Gérer l'ouverture d'un produit en mode édition depuis l'URL
    const editProduitId = searchParams.get('edit');
    const produitId = searchParams.get('produitId');
    const produitToEdit = editProduitId || produitId;
    const tabFromUrl = searchParams.get('tab');
    
    // Vérifier si on est sur l'onglet produits (actif ou dans l'URL)
    if (produitToEdit && (activeTab === 'produits' || tabFromUrl === 'produits')) {
      // S'assurer que l'onglet produits est actif
      if (activeTab !== 'produits' && tabFromUrl === 'produits') {
        setActiveTab('produits');
      }
      
      // Attendre que le composant ProduitsManager soit monté et que activeTab soit 'produits'
      // Utiliser un délai plus long et vérifier plusieurs fois
      let attempts = 0;
      const maxAttempts = 25;
      const checkAndOpen = () => {
        attempts++;
        // Vérifier que le tab dans l'URL est bien 'produits' et que ProduitsManager est monté
        // Ne pas vérifier activeTab car il peut ne pas être encore mis à jour
        if ((activeTab === 'produits' || tabFromUrl === 'produits') && produitsManagerRef.current?.openEditProduit) {
          try {
            produitsManagerRef.current.openEditProduit(produitToEdit);
            // Retirer les paramètres de l'URL après ouverture (en préservant le tab)
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('edit');
            newParams.delete('produitId');
            // S'assurer que le tab est toujours présent
            const currentTab = activeTab === 'produits' ? 'produits' : tabFromUrl;
            if (currentTab && currentTab !== 'overview') {
              newParams.set('tab', currentTab);
            }
            setSearchParams(newParams, { replace: true });
          } catch (err) {
            console.error('[CommercantDetail] Erreur ouverture produit:', err);
          }
        } else if (attempts < maxAttempts) {
          setTimeout(checkAndOpen, 200);
        }
      };
      const timer = setTimeout(checkAndOpen, 600);
      return () => clearTimeout(timer);
    }
  }, [activeTab, searchParams, setSearchParams]);

  // OffreShell / Ajouter élément — ouvrir le manager APRÈS mount/ref (pending non consommé trop tôt).
  useEffect(() => {
    const target = resolvePendingCreateTarget({
      pendingType: pendingCreationType,
      activeTab,
    });
    if (!target) return undefined;

    if (target === 'prestation') {
      return scheduleOpenWhenReady({
        isReady: () => Boolean(servicesManagerRef.current?.openCreatePrestation),
        open: () => {
          const opts = pendingPrestationCreateOptsRef.current || {};
          pendingPrestationCreateOptsRef.current = null;
          servicesManagerRef.current.openCreatePrestation(opts);
        },
        onOpened: () => setPendingCreationType(null),
        onGiveUp: () => {
          pendingPrestationCreateOptsRef.current = null;
          setPendingCreationType(null);
        },
        intervalMs: 50,
        maxAttempts: 40,
      });
    }

    return scheduleOpenWhenReady({
      isReady: () => Boolean(produitsManagerRef.current?.openCreateProduit),
      open: () => {
        produitsManagerRef.current.openCreateProduit();
      },
      onOpened: () => setPendingCreationType(null),
      onGiveUp: () => setPendingCreationType(null),
      intervalMs: 50,
      maxAttempts: 40,
    });
  }, [pendingCreationType, activeTab]);

  // Post-conversion / catalogue — openEditPrestation dès que ServicesManager est monté.
  useEffect(() => {
    const editId = resolvePendingEditTarget({
      pendingEditId: pendingEditPrestationId,
      activeTab,
    });
    if (!editId) return undefined;

    return scheduleOpenWhenReady({
      isReady: () => Boolean(servicesManagerRef.current?.openEditPrestation),
      open: () => {
        servicesManagerRef.current.openEditPrestation(editId);
      },
      onOpened: () => setPendingEditPrestationId(null),
      onGiveUp: () => setPendingEditPrestationId(null),
      intervalMs: 50,
      maxAttempts: 40,
    });
  }, [pendingEditPrestationId, activeTab]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Toasts */}
        {toasts.map((toastItem) => (
          <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
        ))}
        
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">{error}</div>
          <button 
            onClick={() => navigate('/commercants')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Retour aux commerçants
          </button>
        </div>
      </div>
    );
  }

  if (!commercant) {
    return (
      <div className="space-y-6">
        {/* Toasts */}
        {toasts.map((toastItem) => (
          <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
        ))}
        
        <div className="text-center py-12">
          <div className="text-gray-600 mb-4">Commerçant non trouvé</div>
          <button 
            onClick={() => navigate('/commercants')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Retour aux commerçants
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toasts */}
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border" style={{ maxWidth: '1800px', marginLeft: 'auto', marginRight: 'auto' }}>
        {activeTab === 'overview' && validStatDetail && (
          <div className="p-6">
            <OverviewStatDetail
              metric={validStatDetail}
              commandes={commandes}
              prestations={prestations}
              produits={produits}
              analytics={analytics}
              performanceOverview={overviewMois}
              commercant={commercant}
              onBack={closeStatDetail}
              onOpenTab={openTabFromStatDetail}
              onSelectCommande={openCommandeDetail}
              onSelectProduit={openProduitDetail}
              onSelectPrestation={openPrestationDetail}
            />
          </div>
        )}

        {activeTab === 'overview' && !validStatDetail && (
          <div className="p-6">
            {/* Carte boutique — logo/nom/badges à gauche ; CTA public + membre depuis à droite */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
              <div className="flex items-center space-x-4 min-w-0">
                {commercant.logo && (
                  <img 
                    src={cloudinaryDeliveryUrl(commercant.logo, { width: CLOUDINARY_ADMIN_WIDTH.logo }) || commercant.logo} 
                    alt={`Logo ${commercant.nom}`}
                    className="w-16 h-16 rounded-lg object-cover border flex-shrink-0"
                  />
                )}
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 truncate">{commercant.nom}</h2>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {getCommercantCategoryIds(commercant).length > 0 ? (
                      getCommercantCategoryIds(commercant).map((catId) => (
                        <CommerceMetierBadge
                          key={catId}
                          code={catId}
                          getMetier={getTaxonomyMetier}
                          primary={catId === commercant.categorie}
                        />
                      ))
                    ) : (
                      <span className="text-gray-500 text-sm">Métier non défini</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      commercant.actif 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {commercant.actif ? 'Actif' : 'Inactif'}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isOpenNow(commercant)
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {isOpenNow(commercant) ? 'Ouvert' : 'Fermé'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-stretch sm:items-end gap-2 flex-shrink-0">
                {/* Actions super-admin (ex-bandeau) — hors doublon identité */}
                {isSuperAdmin() && !isAssistanceActive() && (
                  <button
                    type="button"
                    disabled={assistanceStarting}
                    onClick={async () => {
                      setAssistanceStarting(true);
                      try {
                        const { context, commercant } = await apiService.startAssistance(id);
                        if (setImpersonatedUser && context) {
                          setImpersonatedUser(context);
                        }
                        toast.success(`Mode assistance : ${commercant?.nom || context.commercantNom}`);
                        navigate(`/commercants/${id}?tab=overview`);
                      } catch (err) {
                        toast.error(err?.message || 'Impossible de démarrer le mode assistance');
                      } finally {
                        setAssistanceStarting(false);
                      }
                    }}
                    className="inline-flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-amber-900 border border-amber-300 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors disabled:opacity-60"
                    title="Se connecter en tant que ce commerçant"
                  >
                    <UserCheck className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {assistanceStarting ? 'Connexion…' : 'Se connecter en tant que'}
                    </span>
                  </button>
                )}
                {isSuperAdmin() && (
                  <button
                    type="button"
                    onClick={() => navigate(`/import-ia?commercantId=${id}`)}
                    className="hidden sm:inline-flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors"
                  >
                    <Sparkles className="h-4 w-4" />
                    <span>Import IA</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    window.open(buildCommercantPublicUrl(commercant ?? { id }), '_blank', 'noopener');
                  }}
                  className="inline-flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  title="Ouvrir la page publique"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden sm:inline">Voir la page publique</span>
                  <span className="sm:hidden">Voir</span>
                </button>
                <div className="text-left sm:text-right">
                  <p className="text-sm text-gray-500">Membre depuis</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(commercant.createdAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>

            {isSuperAdmin() && (
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openTabFromStatDetail('analytics')}
                  className="text-sm text-primary-600 hover:text-primary-800 underline"
                >
                  Analytics détaillés (boutique)
                </button>
                <span className="text-gray-300">|</span>
                <Link
                  to={buildAnalyticsUrl({ commercantId: id, period: '30j', section: 'revenus' })}
                  className="text-sm text-primary-600 hover:text-primary-800 underline"
                >
                  Analytics plateforme (filtré)
                </Link>
              </div>
            )}

            {isSuperAdmin() && nr2jLinkedOrgs.length > 0 ? (
              <div
                className="mb-6 rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3"
                data-testid="nr2j-commercant-reverse-link"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                  Gestion NR2J
                </p>
                <ul className="mt-2 space-y-2">
                  {nr2jLinkedOrgs.map((org) => (
                    <li key={org.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-gray-800">
                        Client / Société NR2J liée :{' '}
                        <span className="font-medium">
                          {org.nomCommercial || org.raisonSociale || org.id}
                        </span>
                      </span>
                      <Link
                        to={`/nr2j/clients/${org.id}`}
                        className="inline-flex rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-50"
                      >
                        Ouvrir la fiche NR2J
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <CommercantSetupChecklist
              commercant={commercant}
              accountStatus={accountStatus}
              produitsCount={analytics.totalProduits || 0}
              prestationsCount={analytics.totalPrestations || 0}
              zonesCount={zonesLivraison.length}
              isSuperAdmin={isSuperAdmin()}
              onNavigateItem={navigateChecklistItem}
            />

            {/* Statistiques — mois civil MTD ; 30j réservé à Analytics (initialOverview) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-1">Performance du mois en cours</h3>
              <ActiviteByTypePanel
                className="mb-3"
                activite={overviewMois?.activite}
                resolver={overviewMois?.resolver}
              />
              {overviewMois?.periode && (
                <p className="text-xs text-gray-400 mb-4">
                  {formatMonthPeriodLabel(overviewMois.periode)}
                </p>
              )}
              {!overviewMois?.periode && <div className="mb-4" />}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <StatCardButton metric="commandes" className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center">
                    <ShoppingCart className="h-8 w-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">
                        {overviewMois?.resolver?.m1Label ||
                          overviewMois?.kpi?.volume?.label ||
                          M1_LABEL_FALLBACK}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {overviewMois?.kpi?.volume?.value ?? '—'}
                      </p>
                      <p className="text-xs text-gray-500">Performance · Voir détail</p>
                    </div>
                  </div>
                </StatCardButton>
                <StatCardButton metric="ca" className="bg-green-50 p-4 rounded-lg border border-green-100">
                  <div className="flex items-center">
                    <Euro className="h-8 w-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">CA encaissé</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {overviewMois != null
                          ? formatEuro(overviewMois.kpi?.ca?.value ?? 0)
                          : '—'}
                      </p>
                      <p className="text-xs text-gray-500">Performance · Voir détail</p>
                    </div>
                  </div>
                </StatCardButton>
                <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-teal-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Ticket moyen</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {overviewMois != null
                          ? formatEuro(overviewMois.kpi?.panierMoyen?.value ?? 0)
                          : '—'}
                      </p>
                      <p className="text-xs text-gray-500">Mois en cours</p>
                    </div>
                  </div>
                </div>
                {moduleProduitsEnabled && (
                  <StatCardButton metric="produits" className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                    <div className="flex items-center">
                      <Package className="h-8 w-8 text-purple-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Produits</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics.totalProduits || 0}</p>
                        <p className="text-xs text-gray-500">En catalogue · Voir détail</p>
                      </div>
                    </div>
                  </StatCardButton>
                )}
                {modulePrestationsEnabled && (
                  <StatCardButton metric="prestations" className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                    <div className="flex items-center">
                      <Users className="h-8 w-8 text-indigo-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Prestations</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics.totalPrestations || 0}</p>
                        <p className="text-xs text-gray-500">En catalogue · Voir détail</p>
                      </div>
                    </div>
                  </StatCardButton>
                )}
                <StatCardButton metric="note" className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                  <div className="flex items-center">
                    <Star className="h-8 w-8 text-yellow-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Note</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.noteMoyenne || 0}/5</p>
                      <p className="text-xs text-gray-500">Moyenne · Voir détail</p>
                    </div>
                  </div>
                </StatCardButton>
              </div>
            </div>

            {/* Configuration des services */}
            <div className="space-y-4">
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Layers className="h-5 w-5 text-blue-600" />
                      <h3 className="text-md font-semibold text-gray-900">Modules activés</h3>
                    </div>
                    {savingModules && (
                      <span className="text-xs text-blue-600">Sauvegarde...</span>
                    )}
                  </div>
                  {canEditPlatformModules() ? (
                    <>
                      <p className="text-sm text-gray-500 mb-4">
                        Réservé à Clickorico : activez les modules contractuels puis cliquez sur{' '}
                        <strong>Enregistrer les modules</strong>. Les permissions POS se synchronisent
                        automatiquement avec le module Caisse.
                      </p>
                      {(!posModuleCoherent || !posPermissionsCoherent) && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                          Incohérence entre module Caisse et permissions POS — ré-enregistrez le module ou
                          ouvrez « Permissions POS ».
                        </p>
                      )}
                      {modulesDirty && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                          Modifications non enregistrées — pensez à valider avant de quitter cette page.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">
                      Ces options sont gérées par Clickorico. Pour activer ou désactiver un module (POS,
                      catalogue…), contactez votre référent Clickorico.
                    </p>
                  )}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-gray-900">Catalogue produits</p>
                        <p className="text-sm text-gray-500">Vente de produits physiques (panier, stock, variantes).</p>
                      </div>
                      {canEditPlatformModules() ? (
                        <label className="relative inline-flex cursor-pointer items-center shrink-0">
                          <input
                            type="checkbox"
                            checked={Boolean(modulesDraft.moduleProduits)}
                            onChange={(e) => handleModuleDraftChange('moduleProduits', e.target.checked)}
                            disabled={savingModules}
                            className="peer sr-only"
                            aria-label="Catalogue produits"
                          />
                          <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
                        </label>
                      ) : (
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            modulesSaved.moduleProduits
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {modulesSaved.moduleProduits ? 'Activé' : 'Non activé'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-gray-900">Prestations & rendez-vous</p>
                        <p className="text-sm text-gray-500">Gestion des services, créneaux personnalisés et réservations.</p>
                      </div>
                      {canEditPlatformModules() ? (
                        <label className="relative inline-flex cursor-pointer items-center shrink-0">
                          <input
                            type="checkbox"
                            checked={Boolean(modulesDraft.modulePrestations)}
                            onChange={(e) => handleModuleDraftChange('modulePrestations', e.target.checked)}
                            disabled={savingModules}
                            className="peer sr-only"
                            aria-label="Prestations et rendez-vous"
                          />
                          <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
                        </label>
                      ) : (
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            modulesSaved.modulePrestations
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {modulesSaved.modulePrestations ? 'Activé' : 'Non activé'}
                        </span>
                      )}
                    </div>
                    <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/50 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900">Caisse (POS)</p>
                          <p className="text-sm text-gray-500">
                            Encaissement comptoir, ventes sur place et file à encaisser (web, RDV).
                          </p>
                        </div>
                        {canEditPlatformModules() ? (
                          <label className="relative inline-flex cursor-pointer items-center shrink-0">
                            <input
                              type="checkbox"
                              checked={Boolean(modulesDraft.moduleCaisse)}
                              onChange={(e) => handleModuleDraftChange('moduleCaisse', e.target.checked)}
                              disabled={savingModules}
                              className="peer sr-only"
                              aria-label="Caisse POS"
                            />
                            <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
                          </label>
                        ) : (
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full shrink-0 ${
                              modulesSaved.moduleCaisse
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {modulesSaved.moduleCaisse ? 'Activé' : 'Non activé'}
                          </span>
                        )}
                      </div>
                      {modulesSaved.moduleCaisse && checkPermission('GESTION_CAISSE') ? (
                        <div className="flex flex-wrap gap-2 pt-1 border-t border-emerald-100">
                          <a
                            href={`/caisse?commercantId=${id}`}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-800 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50"
                          >
                            <Banknote className="h-4 w-4" />
                            Ouvrir la caisse
                          </a>
                          {canEditPlatformModules() && (
                            <button
                              type="button"
                              onClick={() => handleOpenPermissions('pos')}
                              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                              <Shield className="h-4 w-4" />
                              Permissions POS
                            </button>
                          )}
                        </div>
                      ) : canEditPlatformModules() && modulesDraft.moduleCaisse ? (
                        <p className="text-xs text-emerald-700">
                          Caisse activée — cliquez sur « Enregistrer les modules » pour valider définitivement.
                        </p>
                      ) : null}
                      {!canEditPlatformModules() && (
                        <button
                          type="button"
                          onClick={() => handleOpenPermissions('pos')}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <Shield className="h-4 w-4" />
                          Voir mes autorisations
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-gray-900">Expédition</p>
                        <p className="text-sm text-gray-500">
                          Accès au module Expédition colis (configuration commerçant, profils colis). Transporteur et checkout viendront plus tard.
                        </p>
                      </div>
                      {isSuperAdmin() ? (
                        <label className="relative inline-flex cursor-pointer items-center shrink-0">
                          <input
                            type="checkbox"
                            checked={Boolean(modulesDraft.moduleExpedition)}
                            onChange={(e) => handleModuleDraftChange('moduleExpedition', e.target.checked)}
                            disabled={savingModules}
                            className="peer sr-only"
                            aria-label="Module Expédition"
                          />
                          <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
                        </label>
                      ) : (
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            modulesSaved.moduleExpedition
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {modulesSaved.moduleExpedition ? 'Activé' : 'Non activé'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-gray-900">B2B / Tarifs professionnels</p>
                        <p className="text-sm text-gray-500">
                          Le commerçant peut marquer des clients PRO et saisir un tarif HT par produit. Un même client reste particulier sur les autres boutiques.
                        </p>
                      </div>
                      {canEditPlatformModules() ? (
                        <label className="relative inline-flex cursor-pointer items-center shrink-0">
                          <input
                            type="checkbox"
                            checked={Boolean(modulesDraft.moduleB2b)}
                            onChange={(e) => handleModuleDraftChange('moduleB2b', e.target.checked)}
                            disabled={savingModules}
                            className="peer sr-only"
                            aria-label="B2B tarifs professionnels"
                          />
                          <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
                        </label>
                      ) : (
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            modulesSaved.moduleB2b
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {modulesSaved.moduleB2b ? 'Activé' : 'Non activé'}
                        </span>
                      )}
                    </div>
                  </div>
                  {canEditPlatformModules() && (
                  <div className="flex flex-wrap items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={handleCancelModulesDraft}
                      disabled={savingModules || !modulesDirty}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveModules}
                      disabled={savingModules || !modulesDirty}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingModules ? 'Enregistrement…' : 'Enregistrer les modules'}
                    </button>
                  </div>
                  )}
                </div>

                {isSuperAdmin() && (
                  <div id="growth-offer-admin" className="mt-4 space-y-4">
                    <CommunicationOfferAdminPanel
                      commercantId={id}
                      currentTier={growthOffer?.tier || 'INCLUS'}
                      usage={growthOffer?.usage || null}
                      onUpdated={async (res) => {
                        if (res?.entitlements) {
                          setGrowthOffer(res.entitlements);
                          return;
                        }
                        try {
                          const ent = await apiService.getGrowthEntitlements(id);
                          setGrowthOffer(ent);
                        } catch {
                          /* ignore */
                        }
                      }}
                    />
                    <CommunicationBonusAdminPanel
                      commercantId={id}
                      entitlements={growthOffer}
                      onUpdated={async (res) => {
                        if (res?.entitlements) {
                          setGrowthOffer(res.entitlements);
                        }
                      }}
                    />
                  </div>
                )}

                <div id="setup-services">
                <ServicesConfigurationPanel 
                  commercant={commercant}
                  onUpdate={handleUpdateServices}
                  loading={savingServices}
                  onNavigateToTab={openTabFromStatDetail}
                  onNavigateToSetupSection={(section) => navigateChecklistItem({ tab: 'overview', section })}
                  moyensDePaiementSlot={() => (
                    <PolitiquePaiementParModePanel
                      commercant={commercant}
                      onRefresh={fetchCommercantData}
                      toast={toast}
                    />
                  )}
                />
                </div>
            </div>

            {/* Informations générales */}
            <div className="mt-6 scroll-mt-36" id="setup-informations">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Informations générales</h3>
                {!editing && (
                  <button
                    onClick={handleEdit}
                    className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Modifier</span>
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {/* Logo — modifiable sans passer par « Modifier » */}
                  <div id="setup-logo">
                  <CommercantLogoField
                    logoUrl={commercant.logo || null}
                    disabled={uploadingImage}
                    onLogoChange={(url) => handleImageUpload(url, 'logo')}
                    hint="Le logo est enregistré dès l'upload. Pas besoin de cliquer sur Enregistrer."
                  />
                  </div>

                  {/* Bannière (mode édition complet) */}
                  {editing && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bannière
                          {uploadingImage && <span className="ml-2 text-xs text-blue-600">⏳ Sauvegarde en cours...</span>}
                        </label>
                        <CloudinaryUpload
                          onUpload={(urlOrArray) => {
                            const banniereUrl = Array.isArray(urlOrArray) ? urlOrArray[0] : urlOrArray;
                            handleImageUpload(banniereUrl, 'banniere');
                          }}
                          currentImages={editData.banniere ? [editData.banniere] : []}
                          maxImages={1}
                        />
                        {editData.banniere && (
                          <p className="text-xs text-green-600 mt-1 flex items-center">
                            ✅ Bannière sauvegardée automatiquement
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom du commerçant</label>
                    {editing && currentUserRole !== 'COMMERCANT' ? (
                      <input
                        type="text"
                        value={editData.nom}
                        onChange={(e) => setEditData({...editData, nom: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Nom du commerçant"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{editing ? editData.nom : commercant.nom}</p>
                    )}
                    {currentUserRole === 'COMMERCANT' ? (
                      <p className="mt-1 text-xs text-gray-500">Modification gérée par Clickorico</p>
                    ) : null}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email de contact</label>
                    {editing ? (
                      <input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Adresse e-mail de contact"
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md flex-1">{commercant.email}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email notifications commandes</label>
                    {editing ? (
                      <>
                        <input
                          type="email"
                          value={editData.emailNotificationCommandes ?? ''}
                          onChange={(e) => setEditData({ ...editData, emailNotificationCommandes: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Adresse e-mail pour les alertes de commandes"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Adresse utilisée pour les alertes de nouvelles commandes. Si vide, l&apos;email de contact est utilisé.
                        </p>
                      </>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md flex-1">
                          {commercant.emailNotificationCommandes || 'Non renseigné (email de contact utilisé)'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    {editing ? (
                      <input
                        type="tel"
                        value={editData.telephone}
                        onChange={(e) => setEditData({...editData, telephone: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Numéro de téléphone"
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md flex-1">
                          {commercant.telephone || 'Non renseigné'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Site web</label>
                    {editing ? (
                      <input
                        type="url"
                        value={editData.siteWeb}
                        onChange={(e) => setEditData({...editData, siteWeb: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="URL du site web (facultatif)"
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4 text-gray-400" />
                        {commercant.siteWeb ? (
                          <a 
                            href={commercant.siteWeb} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 bg-gray-50 px-3 py-2 rounded-md flex-1"
                          >
                            {commercant.siteWeb}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-md flex-1">Non renseigné</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Adresse complète (lecture seule) ou champs séparés (édition) */}
                  {editing ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rue</label>
                        <input
                          type="text"
                          value={editData.rue}
                          onChange={(e) => setEditData({...editData, rue: e.target.value})}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Numéro et nom de rue"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                          <input
                            type="text"
                            value={editData.codePostal}
                            onChange={(e) => setEditData({...editData, codePostal: e.target.value})}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="45000"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                          <input
                            type="text"
                            value={editData.ville}
                            onChange={(e) => setEditData({...editData, ville: e.target.value})}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nom de la ville"
                          />
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="text-sm font-semibold text-slate-800">Localisation GPS</p>
                          <p className="mt-1 text-xs text-slate-600">
                            L’édition des coordonnées (latitude, longitude, correction manuelle) a été déplacée
                            vers le panneau « Localisation et qualité des coordonnées » réservé aux
                            administrateurs Clickorico. Vous pouvez toujours modifier l’adresse ci-dessus ;
                            la position sera revue côté qualité.
                          </p>
                          {(hasMainCoordinates || hasAdjustedCoordinates) && (
                            <div className="mt-3 flex flex-wrap gap-2 text-xs">
                              {hasMainCoordinates && (
                                <span className="rounded-md bg-white px-2 py-1 text-slate-700 border border-slate-200">
                                  Auto {formatCoordinate(commercant.latitudeOriginal ?? commercant.latitude)} /{' '}
                                  {formatCoordinate(commercant.longitudeOriginal ?? commercant.longitude)}
                                </span>
                              )}
                              {hasAdjustedCoordinates && (
                                <span className="rounded-md bg-blue-50 px-2 py-1 text-blue-800 border border-blue-100">
                                  Override {formatCoordinate(commercant.latitudeOverride)} /{' '}
                                  {formatCoordinate(commercant.longitudeOverride)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse complète</label>
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md flex-1">
                          {commercant.rue && commercant.ville ? 
                            `${commercant.rue}, ${commercant.codePostal} ${commercant.ville}` : 
                            'Adresse non renseignée'
                          }
                        </p>
                      </div>
                      {(hasMainCoordinates || mainNavigationLinks.length > 0) && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {hasMainCoordinates && (
                            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                              Lat {formatCoordinate(commercant.latitude)} • Lon {formatCoordinate(commercant.longitude)}
                            </span>
                          )}
                          {hasAdjustedCoordinates && (
                            <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                              Ajustée manuellement • Auto {formatCoordinate(commercant.latitudeOriginal)} / {formatCoordinate(commercant.longitudeOriginal)}
                            </span>
                          )}
                          {mainNavigationLinks.map((link) => (
                            <a
                              key={link.id}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-blue-700"
                            >
                              <Navigation className="h-3.5 w-3.5" />
                              {link.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
                    {editing ? (
                      <>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Mode itinérant</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Activez pour définir une adresse différente pour chaque créneau d’ouverture.
                            </p>
                          </div>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editData.magasinItinerant}
                              onChange={(e) => setEditData({
                                ...editData,
                                magasinItinerant: e.target.checked
                              })}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Commerce itinérant</span>
                          </label>
                        </div>
                        <p className="mt-3 text-xs text-gray-700">
                          {editData.magasinItinerant
                            ? 'Les lieux et adresses configurés dans les horaires seront utilisés comme points de retrait. L’adresse principale reste uniquement l’adresse du commerce.'
                            : 'L’adresse principale sera utilisée comme point de retrait des commandes.'}
                        </p>
                        {editData.magasinItinerant && commercant?.clickAndCollect && !hasItinerantRetraitAddress(commercant?.horaires) && (
                          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <p>
                              Aucun point de retrait itinérant n’est encore configuré. Ajoutez au moins un lieu et une adresse dans les{' '}
                              <a href="#setup-horaires" className="font-semibold underline hover:text-amber-950">
                                horaires
                              </a>{' '}
                              avant d’ouvrir les commandes en Click &amp; Collect.
                            </p>
                          </div>
                        )}
                        {editData.magasinItinerant && (
                          <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Lieu principal (optionnel)
                            </label>
                            <input
                              type="text"
                              value={editData.lieuPrincipal}
                              onChange={(e) => setEditData({...editData, lieuPrincipal: e.target.value})}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Adresse de référence, dépôt, siège administratif…"
                            />
                            <p className="text-xs text-gray-500">
                              Ce lieu sert de repère général ; l’adresse exacte est définie dans chaque créneau.
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          commercant.magasinItinerant
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {commercant.magasinItinerant ? 'Commerce itinérant' : 'Point de vente fixe'}
                        </span>
                        <p className="text-sm text-gray-700">
                          {commercant.magasinItinerant
                            ? 'Les lieux et adresses configurés dans les horaires seront utilisés comme points de retrait. L’adresse principale reste uniquement l’adresse du commerce.'
                            : 'L’adresse principale sera utilisée comme point de retrait des commandes.'}
                        </p>
                        {commercant.magasinItinerant && commercant.clickAndCollect && !hasItinerantRetraitAddress(commercant?.horaires) && (
                          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <p>
                              Aucun point de retrait itinérant n’est encore configuré. Ajoutez au moins un lieu et une adresse dans les{' '}
                              <a href="#setup-horaires" className="font-semibold underline hover:text-amber-950">
                                horaires
                              </a>{' '}
                              avant d’ouvrir les commandes en Click &amp; Collect.
                            </p>
                          </div>
                        )}
                        {commercant.lieuPrincipal && (
                          <p className="text-xs text-gray-500">
                            Lieu principal : {commercant.lieuPrincipal}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    {editing ? (
                      <textarea
                        value={editData.description}
                        onChange={(e) => setEditData({...editData, description: e.target.value})}
                        rows={4}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Description du commerçant"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md min-h-[80px]">
                        {commercant.description || 'Aucune description disponible'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Award className="h-4 w-4 text-gray-400 shrink-0" />
                      {getCommercantCategoryIds(commercant).length > 0 ? (
                        getCommercantCategoryIds(commercant).map((catId) => (
                          <CommerceMetierBadge
                            key={catId}
                            code={catId}
                            getMetier={getTaxonomyMetier}
                            primary={catId === commercant.categorie}
                          />
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">Non définie</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isCommercantAdmin() && (
                <CoordinateQualityPanel
                  commercantId={id}
                  enabled
                  toast={toast}
                  onChanged={fetchCommercantData}
                />
              )}

              {/* Boutons d'action pour l'édition */}
              {editing && (
                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCancel}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    <X className="h-4 w-4" />
                    <span>Annuler</span>
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4" />
                    <span>Sauvegarder</span>
                  </button>
                </div>
              )}
            </div>

            {/* Horaires */}
            <div className="mt-6 scroll-mt-36" id="setup-horaires">
              <h3 className="text-lg font-semibold mb-4">Horaires d'ouverture</h3>
              <HorairesManager 
                horaires={commercant?.horaires ?? []}
                onSave={handleSaveHoraires}
                loading={savingHoraires}
                isItinerant={editing ? Boolean(editData.magasinItinerant) : Boolean(commercant?.magasinItinerant)}
              />
            </div>

            {/* Fermetures temporaires */}
            <div className="mt-6">
              <FermeturesTemporaires 
                commercantId={id}
                fermetures={fermeturesTemporaires}
                onUpdate={handleUpdateFermetures}
                loading={savingFermetures}
              />
            </div>
          </div>
        )}

        {activeTab === 'clickandcollect' && (
          <div className="p-6">
            <ClickAndCollectManager
              commercantId={id}
              onSettingsUpdated={handleClickAndCollectUpdate}
              onRefresh={fetchCommercantData}
            />
          </div>
        )}

        {activeTab === 'expedition' && commercant?.moduleExpedition && (
          <div className="p-6">
            {isSuperAdmin() ? <ShippingIntegrationPanel commercantId={id} toast={toast} /> : null}
            <ExpeditionManager commercantId={id} />
          </div>
        )}

        {activeTab === 'livraison' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_LIVRAISON"
              title="Livraison"
              icon="🚚"
              description="Configurez les modes de livraison et les créneaux proposés à vos clients"
              features={[
                'Modes Dès que possible et/ou sur créneaux',
                'Créneaux en fenêtres complètes',
                'Capacité par créneau',
                'Préremplissage depuis les horaires (add-only)',
              ]}
            >
              <LivraisonManager
                commercant={commercant}
                onUpdate={handleUpdateServices}
                loading={savingServices}
                onNavigateToTab={openTabFromStatDetail}
              />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'livreurs' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_LIVRAISON"
              title="Gestion des livreurs"
              icon="🚚"
              description="Gérez votre équipe de livreurs et optimisez vos livraisons avec un suivi en temps réel"
              features={[
                'Gestion complète des livreurs',
                'Suivi en temps réel des livraisons',
                'Affectation automatique des commandes',
                'Statistiques de performance',
                'Planification et optimisation des tournées'
              ]}
            >
              <LivreursManager commercantId={id} commercant={commercant} onCommercantUpdate={fetchCommercantData} />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'reservations-tables' && commercant?.reservationTable && (
          <div className="p-6">
            <ReservationsTablesManager commercantId={id} commercant={commercant} />
          </div>
        )}

        {activeTab === 'prestations' && modulePrestationsEnabled && (
          <div className="p-6 space-y-6">
            <ServicesManager
              ref={servicesManagerRef}
              commercant={commercant}
              onAddPrestation={() => triggerCreationFlow('prestation')}
              onUpdate={handleUpdateServices}
              onSaveCommercantHoraires={handleSaveHoraires}
              savingHoraires={savingHoraires}
              embeddedAddMode={false}
              loading={savingServices}
            />
          </div>
        )}

        {activeTab === 'ressources' && (commercant?.moduleCreneaux || commercant?.priseCreneaux) && (
          <div className="p-6">
            <RessourcesManager
              commercant={commercant}
              onUpdateCommercant={handleUpdateServices}
              loading={savingServices}
            />
          </div>
        )}

        {activeTab === 'produits' && moduleProduitsEnabled && (
          <div className="p-6 space-y-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_PRODUITS"
              title="Gestion des produits"
              icon="🍽️"
              description="Créez, modifiez et gérez votre catalogue de produits avec toutes les fonctionnalités avancées"
              features={[
                'Création et modification de produits',
                'Gestion des catégories et variantes',
                'Gestion des prix et promotions',
                'Upload et synchronisation d\'images',
                'Gestion de la disponibilité'
              ]}
            >
              <ImageSyncStatus commercantId={id} />
              <CatalogueRubriquesManager
                commercantId={id}
                produits={produits}
                onCatalogueChanged={() => produitsManagerRef.current?.refresh?.()}
              />
              <ProduitsManager
                ref={produitsManagerRef}
                commercantId={id}
                // Ne pas passer onAddProduit pour laisser ProduitsManager gérer son propre modal
                // OffreShell O1 : Prestation réservable → Mes Prestations (openCreatePrestation)
                onRequestPrestationReservable={(opts) =>
                  triggerCreationFlow('prestation', opts)
                }
                onRequestEditPrestation={(prestationId) => {
                  if (!prestationId || !modulePrestationsEnabled) return;
                  const id = String(prestationId).trim();
                  if (!id) return;
                  // Orchestration déterministe (scheduleOpenWhenReady) — pas de setTimeout d’open.
                  setPendingEditPrestationId(id);
                  isUpdatingToUrlRef.current = true;
                  setActiveTab('prestations');
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('tab', 'prestations');
                  newParams.delete('editPrestation');
                  setSearchParams(newParams, { replace: true });
                  setTimeout(() => {
                    isUpdatingToUrlRef.current = false;
                  }, 100);
                }}
                embeddedAddMode={false}
              />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'cartes-cadeaux' && (
          <div className="p-6">
            <CartesCadeauxManager commercantId={id} />
          </div>
        )}

        {activeTab === 'commandes' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_COMMANDES"
              title="Gestion des commandes"
              icon="📦"
              description="Suivez et traitez toutes vos commandes en temps réel avec un système de gestion complet"
              features={[
                'Suivi des commandes en temps réel',
                'Gestion des statuts de commande',
                'Historique complet des commandes',
                'Filtres et recherches avancées',
                'Export et rapports de commandes'
              ]}
            >
              <CommercantCommandes
                commercantId={id}
                initialCommandeId={searchParams.get('commandeId')}
                onCommandeDetailClosed={clearCommandeDetailFromUrl}
                onDataChanged={refreshOverview30j}
              />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'promotions' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_PROMOTIONS"
              title="Gestion des promotions"
              icon="🎯"
              description="Créez et gérez des promotions attractives pour booster vos ventes et fidéliser vos clients"
              features={[
                'Création de promotions et codes promo',
                'Réductions en pourcentage ou montant fixe',
                'Promotions conditionnelles (montant minimum, etc.)',
                'Gestion des dates et périodes',
                'Statistiques des promotions'
              ]}
            >
              <PromotionsManager
                commercantId={id}
                commercantName={commercant?.nom}
                embedded
              />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'avis' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_AVIS"
              title="Gestion des avis"
              icon="⭐"
              description="Modérez et répondez aux avis de vos clients pour améliorer votre réputation"
              features={[
                'Modération des avis clients',
                'Réponse aux avis avec personnalisation',
                'Statistiques des avis',
                'Gestion des avis signalés',
                'Export des avis'
              ]}
            >
              <AvisManager commercantId={id} commercant={commercant} />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_ANALYTICS"
              title="Analytics et rapports"
              icon="📊"
              description="Analysez vos performances avec des tableaux de bord détaillés et des rapports personnalisables"
              features={[
                'Tableaux de bord en temps réel',
                'Statistiques de ventes et revenus',
                'Analyse du trafic et des conversions',
                'Rapports personnalisables',
                'Export de données et graphiques'
              ]}
            >
              <CommercantAnalytics
                commercantId={id}
                initialOverview={overview30j}
                moduleCaisse={moduleCaisseEnabled}
              />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'fidelite' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_FIDELITE"
              title="Programme de fidélité"
              icon="⭐"
              description="Créez et gérez un programme de fidélité pour récompenser vos clients réguliers"
              features={[
                'Système de points et de niveaux',
                'Badges et récompenses',
                'Statistiques du programme',
                'Configuration des règles de fidélité',
                'Historique des gains clients'
              ]}
            >
              <FideliteSectionNav
                activeSection={fideliteSection}
                onSelect={setFideliteSection}
              />
              {fideliteSection === FIDELITE_SECTION_RECOMPENSES ? (
                <FideliteRecompenses commercantId={id} />
              ) : (
                <ProgrammeFidelite
                  commercantId={id}
                  onManageRecompenses={() => setFideliteSection(FIDELITE_SECTION_RECOMPENSES)}
                />
              )}
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_CHAT"
              title="Chat avec les clients"
              icon="💬"
              description="Communiquez en temps réel avec vos clients pour répondre à leurs questions et améliorer leur expérience"
              features={[
                'Chat en temps réel avec vos clients',
                'Gestion de plusieurs conversations simultanées',
                'Statut en ligne/hors ligne',
                'Historique complet des conversations',
                'Notifications de nouveaux messages'
              ]}
            >
              <div className="space-y-6">
                {/* Toggle activation chat */}
                <div className="bg-gray-50 border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Activer le chat</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {commercant?.chatActif 
                          ? 'Le chat est activé et disponible pour vos clients'
                          : 'Activez le chat pour permettre à vos clients de vous contacter en direct'}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const newChatActif = !commercant?.chatActif;
                          const response = await apiService.updateCommercantChat(id, newChatActif);
                          console.log('✅ Chat activé/désactivé:', response);
                          // Recharger toutes les données du commerçant pour avoir la dernière valeur
                          await fetchCommercantData();
                          toast.success(newChatActif ? 'Chat activé avec succès !' : 'Chat désactivé');
                        } catch (err) {
                          console.error('Erreur activation chat:', err);
                          const errorMsg = err?.response?.data?.error || err?.message || 'Erreur lors de l\'activation du chat';
                          toast.error(errorMsg.includes('permission') || errorMsg.includes('Permission') 
                            ? 'Vous devez avoir la permission GESTION_CHAT pour activer le chat. Contactez un administrateur.'
                            : errorMsg);
                        }
                      }}
                      className="flex items-center space-x-2"
                    >
                      {commercant?.chatActif ? (
                        <ToggleRight className="h-8 w-8 text-green-600" />
                      ) : (
                        <ToggleLeft className="h-8 w-8 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {commercant?.chatActif && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-4">
                        Le chat est activé. Vous pouvez maintenant gérer vos conversations depuis le menu "Chat".
                      </p>
                      <button
                        onClick={() => navigate('/chat')}
                        className="btn-primary"
                      >
                        Ouvrir le chat
                      </button>
                    </div>
                  )}
                </div>
                
                {!commercant?.chatActif && (
                  <div className="text-center py-8">
                    <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Chat non activé</h3>
                    <p className="text-gray-600 mb-4">
                      Activez le chat ci-dessus pour commencer à communiquer avec vos clients.
                    </p>
                  </div>
                )}
              </div>
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'communication' && (
          <div className="p-0">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_NOTIFICATIONS"
              title="Communication"
              icon="🔔"
              description="Informez et fidélisez vos clients via les notifications dans l’application et l’email marketing (hors emails de commande)."
              features={[
                'Notifications dans l’application client',
                'Campagnes email marketing',
                'Historique des envois',
                'Offres Inclus et Plus',
              ]}
            >
              <Notifications commercantId={id} commerceName={commercant?.nom} />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'zones' && (
          <div className="p-6">
            <PremiumFeatureCard
              commercantId={id}
              permission="GESTION_LIVRAISON"
              title="Zones de livraison"
              icon="📍"
              description="Définissez vos zones de livraison avec précision et gérez les frais de livraison par zone"
              features={[
                'Définition de zones géographiques',
                'Gestion des frais de livraison par zone',
                'Montants minimums et livraison gratuite',
                'Délais de livraison personnalisés',
                'Cartographie interactive des zones'
              ]}
            >
              <ZonesLivraisonManager commercantId={id} />
            </PremiumFeatureCard>
          </div>
        )}

        {activeTab === 'stock' && shouldShowStockNavigation(commercant) && (
          <div className="p-6">
            {!canUseStockManagement(commercant, commercantPermissions) ? (
              <DisabledServiceFeatureCard
                title="Ingrédients & ruptures"
                icon="📦"
                description="Signalez une matière première indisponible : les produits liés deviennent non commandables. Distinct du stock limité sur la fiche produit."
                features={[
                  'Ingrédients et matières premières',
                  'Mise en rupture / remise disponible',
                  'Impact sur les produits liés',
                  'Historique des ruptures',
                ]}
                contactMessage="Pour activer les ruptures par ingrédient sur votre boutique, contactez votre administrateur Clickorico."
                settingsHint={isSuperAdmin()
                  ? 'Vous pouvez l\'activer tout de suite dans Paramètres → Décompte du stock produit de cette boutique.'
                  : 'Une fois activée, vous pourrez gérer les ruptures d’ingrédients depuis cette page.'}
              />
            ) : (
              <PremiumFeatureCard
                commercantId={id}
                permission="GESTION_STOCK"
                title="Ingrédients & ruptures"
                icon="📦"
                description="Signalez une matière première indisponible : les produits liés deviennent non commandables."
                features={[
                  'Ingrédients et matières premières',
                  'Mise en rupture / remise disponible',
                  'Impact sur les produits liés',
                  'Historique des ruptures',
                ]}
              >
                <IngredientsManager
                  commercantId={id}
                  isSuperAdmin={isPlatformIngredientsReadOnly(currentUser)}
                />
              </PremiumFeatureCard>
            )}
          </div>
        )}

        {activeTab === 'identifiants' && isSuperAdmin() && (
          <div className="p-6">
            <IdentifiantsCommercant 
              commercantId={id}
              commercantEmail={commercant.email}
              commercantNom={commercant.nom}
            />
          </div>
        )}

        {activeTab === 'impression' && isSuperAdmin() && (
          <div className="p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Impression — {commercant.nom}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Cockpit technique du point de vente (même composant que la
                fiche d&apos;édition Commerçants). Inventaire et affectation :
                page Matériels.
              </p>
            </div>
            <CommercantPrinterCockpit
              commercantId={id}
              value={impressionDraft}
              appKeySaveNonce={appKeySaveNonce}
              appKeySavedOnLastSave={appKeySavedOnLastSave}
              onChange={(partial) =>
                setImpressionDraft((prev) => ({ ...prev, ...partial }))
              }
            />
            <div className="flex justify-end border-t border-gray-100 pt-4">
              <button
                type="button"
                disabled={savingImpression}
                onClick={handleSaveImpression}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60"
              >
                <Save className="h-4 w-4" aria-hidden />
                {savingImpression ? 'Enregistrement…' : 'Enregistrer l’impression'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'parametres' && (
          <CommercantSettingsPanel
            commercantId={id}
            commercant={commercant}
            isSuperAdmin={isSuperAdmin()}
            onCommercantUpdate={(data) => {
              setCommercant((prev) => (prev ? { ...prev, ...data } : prev));
              window.dispatchEvent(
                new CustomEvent('commercantServicesUpdated', {
                  detail: { commercantId: id, services: data },
                }),
              );
            }}
            onOpenEstablishment={() => openOverviewSection('informations')}
            onOpenHours={() => openOverviewSection('horaires')}
          />
        )}

        {activeTab === 'notifications' && (
          <CommercantSettingsPanel
            commercantId={id}
            commercant={commercant}
            isSuperAdmin={isSuperAdmin()}
            onCommercantUpdate={(data) => {
              setCommercant((prev) => (prev ? { ...prev, ...data } : prev));
            }}
            onOpenEstablishment={() => openOverviewSection('informations')}
            onOpenHours={() => openOverviewSection('horaires')}
          />
        )}

        {activeTab === 'advanced' && isSuperAdmin() && (
          <div className="p-6">
            <ParametresAvances 
              commercant={commercant} 
              onUpdate={handleUpdateAdvanced} 
              onDelete={handleDeleteCommercant}
              loading={savingAdvanced}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        title="Ajouter un élément"
        maxWidth="max-w-xl"
      >
        <div className="space-y-6">
          <div>
            <p className="text-sm text-gray-500">
              Choisissez le type d’élément à créer pour <span className="font-medium text-gray-700">{commercant.nom}</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {moduleProduitsEnabled && (
              <button
                type="button"
                onClick={() => setSelectedItemType('produit')}
                className={`w-full text-left rounded-lg p-4 transition border ${
                  selectedItemType === 'produit'
                    ? 'border-green-500 bg-green-50 shadow-sm'
                    : 'border-gray-200 hover:border-green-400 hover:bg-green-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Produit</p>
                    <p className="text-sm text-gray-500">
                      Article physique avec images, variantes, stock et ingrédients.
                    </p>
                  </div>
                  <Package
                    className={`h-6 w-6 ${
                      selectedItemType === 'produit' ? 'text-green-600' : 'text-gray-300'
                    }`}
                  />
                </div>
              </button>
            )}

            {modulePrestationsEnabled && (
              <button
                type="button"
                onClick={() => setSelectedItemType('prestation')}
                className={`w-full text-left rounded-lg p-4 transition border ${
                  selectedItemType === 'prestation'
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Activité</p>
                    <p className="text-sm text-gray-500">
                      Réservation guidée (recommandé) ou formulaire avancé — terrains, cours, créneaux.
                    </p>
                  </div>
                  <Users
                    className={`h-6 w-6 ${
                      selectedItemType === 'prestation' ? 'text-blue-600' : 'text-gray-300'
                    }`}
                  />
                </div>
              </button>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowAddItemModal(false)}
              className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => triggerCreationFlow(selectedItemType)}
              disabled={!selectedItemType}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              Continuer
            </button>
          </div>
        </div>
      </Modal>

      {showPermissionsModal && commercant && (
        <PermissionModal
          commercant={commercant}
          permissions={commercantPermissions}
          initialCategory={permissionModalCategory}
          readOnly={!canEditPlatformModules()}
          onClose={() => setShowPermissionsModal(false)}
          onSave={handleSavePermissions}
        />
      )}
    </div>
  );
};

export default CommercantDetail;
